import {
  Almanac,
  Category,
  Role,
  roleFromJSON,
} from "protos/robot_syncer/plant_profile";
import { CategorizedImagesVersus } from "../categorized/CategorizedImages";
import {
  DEFAULT_CREATE_CATEGORY_ROLE,
  PLANT_PROFILE_ROLES,
  resolveCategoryRole,
} from "portal/utils/categoryCollection/category";
import { getVersionShootTime, VersionShootTime } from "./versionShootTime";
import { RadioChipOption } from "portal/components/RadioChips";

export interface VersionCategories {
  categories: Category[];
  options: RadioChipOption[];
  roles?: Record<string, Role>;
}

type CategoryById = {
  chipIds: string[];
  almanac?: Almanac;
  name?: string;
};

const byCategoryId = ({
  categories,
  options,
}: VersionCategories): Map<string, CategoryById> => {
  const byId = new Map<string, CategoryById>(
    categories.map((category) => [
      category.id,
      { chipIds: category.chipIds, almanac: category.almanac },
    ])
  );
  for (const { id, name } of options) {
    const row = byId.get(id);
    if (row) {
      row.name = name;
    }
  }
  return byId;
};

export interface VersionCategoryCard {
  categoryId: string;
  name: string;
  predictionPointIds: string[];
  versus?: CategorizedImagesVersus;
  shootTime?: VersionShootTime;
  role?: Role; // Omitted when the category was removed.
}

/** Section to list a card under: selected-version role, or parent role if removed. */
export const historySectionRole = (card: VersionCategoryCard): Role =>
  (card.versus?.categoryChange === "removed" ? card.versus.role : card.role) ??
  roleFromJSON(DEFAULT_CREATE_CATEGORY_ROLE);

export const groupVersionCategoryCardsByRole = (
  cards: VersionCategoryCard[]
): Map<Role, VersionCategoryCard[]> => {
  const byRole = new Map<Role, VersionCategoryCard[]>();
  for (const role of PLANT_PROFILE_ROLES) {
    byRole.set(role, []);
  }
  for (const card of cards) {
    byRole.get(historySectionRole(card))?.push(card);
  }
  return byRole;
};

export const getVersionCategoryCards = (
  version: VersionCategories,
  versus?: VersionCategories
): VersionCategoryCard[] => {
  const currentById = byCategoryId(version);
  const versusById = versus ? byCategoryId(versus) : undefined;

  const cards: VersionCategoryCard[] = version.options.map(({ id, name }) => {
    const current = currentById.get(id);
    const compared = versusById?.get(id);
    const afterRole = resolveCategoryRole(
      id,
      current?.name ?? name,
      version.roles ?? {}
    );
    const beforeRole = versusById?.has(id)
      ? resolveCategoryRole(id, compared?.name, versus?.roles ?? {})
      : undefined;
    const shootTime = getVersionShootTime(
      current?.almanac,
      versus ? { almanac: compared?.almanac } : undefined,
      afterRole
    );
    return {
      categoryId: id,
      name,
      predictionPointIds: current?.chipIds ?? [],
      versus: versus
        ? {
            name: compared?.name,
            chipIds: compared?.chipIds ?? [],
            categoryChange: versusById?.has(id) ? undefined : "added",
            shootTimeChangeCounts: shootTime.changes,
            ...(beforeRole !== undefined ? { role: beforeRole } : {}),
          }
        : undefined,
      shootTime: shootTime.bands.length > 0 ? shootTime : undefined,
      role: afterRole,
    };
  });

  if (!versus || !versusById) {
    return cards;
  }

  const versionIds = new Set(version.options.map((option) => option.id));
  for (const { id, name } of versus.options) {
    if (versionIds.has(id)) {
      continue;
    }
    const compared = versusById.get(id);
    const beforeRole = resolveCategoryRole(
      id,
      compared?.name ?? name,
      versus.roles ?? {}
    );
    const shootTime = getVersionShootTime(
      undefined,
      {
        almanac: compared?.almanac,
      },
      beforeRole
    );
    cards.push({
      categoryId: id,
      name,
      predictionPointIds: [],
      versus: {
        name,
        chipIds: compared?.chipIds ?? [],
        categoryChange: "removed",
        shootTimeChangeCounts: shootTime.changes,
        role: beforeRole,
      },
      shootTime: shootTime.bands.length > 0 ? shootTime : undefined,
    });
  }
  return cards;
};

/** Single library-category snapshot. Shoot time is never role-gated. */
export const getTemplateVersionCard = (
  category: Category,
  versus?: Category
): VersionCategoryCard => {
  const shootTime = getVersionShootTime(
    category.almanac,
    versus ? { almanac: versus.almanac } : undefined
  );
  return {
    categoryId: category.id,
    name: category.name,
    predictionPointIds: category.chipIds,
    versus: versus
      ? {
          name: versus.name,
          chipIds: versus.chipIds,
          shootTimeChangeCounts: shootTime.changes,
        }
      : undefined,
    shootTime: shootTime.bands.length > 0 ? shootTime : undefined,
  };
};
